/**
 * Auth Store - Manages authentication state
 *
 * Uses Preact Signals for reactive state management.
 * Single source of truth for all auth-related state.
 * Supports multiple music providers: Spotify, Apple Music, YouTube Music, Amazon Music.
 */

import { signal, computed, effect } from '@preact/signals';
import type { User, AuthTokens, LoginCredentials, RegisterData, MusicProvider, ProviderConnection, ProviderInfo } from '@/types';
import { api } from '@/api/client';

// ============================================
// PROVIDER CONFIGURATION
// ============================================

export const PROVIDERS: ProviderInfo[] = [
  {
    id: 'spotify',
    name: 'Spotify',
    icon: 'spotify',
    authUrl: '/api/music/auth/spotify',
    color: '#1DB954',
    enabled: true,
  },
  {
    id: 'apple_music',
    name: 'Apple Music',
    icon: 'apple_music',
    authUrl: '/api/music/auth/apple_music',
    color: '#FC3C44',
    enabled: true,
  },
  {
    id: 'youtube_music',
    name: 'YouTube Music',
    icon: 'youtube',
    authUrl: '/api/music/auth/youtube',
    color: '#FF0000',
    enabled: false, // Coming soon
  },
  {
    id: 'amazon_music',
    name: 'Amazon Music',
    icon: 'amazon',
    authUrl: '/api/music/auth/amazon',
    color: '#00A8E1',
    enabled: false, // Coming soon
  },
];

// ============================================
// STATE
// ============================================

// Core auth state
export const user = signal<User | null>(null);
export const tokens = signal<AuthTokens | null>(null);
export const isLoading = signal(true); // Start true, check auth on mount
export const error = signal<string | null>(null);

// Music provider connections (multi-provider support)
export const providerConnections = signal<Map<MusicProvider, ProviderConnection>>(new Map());
export const activeProvider = signal<MusicProvider | null>(null);

// Legacy Spotify-specific (for backward compatibility during migration)
export const spotifyConnected = computed(() => {
  const conn = providerConnections.value.get('spotify');
  return conn?.connected || false;
});
export const spotifyTokens = computed(() => {
  const conn = providerConnections.value.get('spotify');
  if (!conn?.connected || !conn.accessToken) return null;
  return {
    accessToken: conn.accessToken,
    refreshToken: conn.refreshToken || '',
    expiresAt: conn.expiresAt || 0,
  };
});

// ============================================
// COMPUTED
// ============================================

export const isAuthenticated = computed(() => !!user.value && !!tokens.value);

// Check if any music provider is connected
export const hasAnyMusicProvider = computed(() => {
  for (const [, conn] of providerConnections.value) {
    if (conn.connected) return true;
  }
  return false;
});

// Get list of connected providers
export const connectedProviders = computed(() => {
  const connected: MusicProvider[] = [];
  for (const [provider, conn] of providerConnections.value) {
    if (conn.connected) connected.push(provider);
  }
  return connected;
});

// Check if specific provider is ready for use
export const isProviderReady = (provider: MusicProvider) => {
  return computed(() => {
    if (!isAuthenticated.value) return false;
    const conn = providerConnections.value.get(provider);
    return conn?.connected || false;
  });
};

// Legacy - for backward compat
export const isSpotifyReady = computed(() => isAuthenticated.value && spotifyConnected.value);

// ============================================
// PERSISTENCE
// ============================================

const TOKEN_KEY = 'sherit_tokens';
const USER_KEY = 'sherit_user';

function saveToStorage() {
  if (tokens.value) {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens.value));
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
  if (user.value) {
    localStorage.setItem(USER_KEY, JSON.stringify(user.value));
  } else {
    localStorage.removeItem(USER_KEY);
  }
}

function loadFromStorage() {
  try {
    const savedTokens = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);

    if (savedTokens) {
      const parsed = JSON.parse(savedTokens) as AuthTokens;
      // Check if token is expired
      if (parsed.expiresAt > Date.now()) {
        tokens.value = parsed;
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    if (savedUser) {
      user.value = JSON.parse(savedUser) as User;
    }
  } catch (e) {
    console.error('Failed to load auth from storage:', e);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }
}

// Auto-save on changes
effect(() => {
  // Subscribe to both signals (accessing .value triggers effect re-runs)
  tokens.value;
  user.value;
  saveToStorage();
});

// ============================================
// ACTIONS
// ============================================

/**
 * Initialize auth state on app load
 */
export async function initAuth(): Promise<void> {
  isLoading.value = true;
  error.value = null;

  // Load from storage first
  loadFromStorage();

  // Check for OAuth callback params in URL
  const urlParams = new URLSearchParams(window.location.search);
  const provider = urlParams.get('provider');

  // Handle Apple Music redirect (legacy/fallback - now handled by direct click)
  if (provider === 'apple_music') {
    // Clean URL and let user click the button manually
    // (Popup would be blocked if triggered during page load)
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('provider');
    cleanUrl.searchParams.delete('state');
    window.history.replaceState({}, '', cleanUrl.toString());
    // Don't block - let user click Apple Music button manually
  }

  // Handle Spotify (and other OAuth) callback tokens
  const accessToken = urlParams.get('accessToken') || urlParams.get('token');
  if (accessToken) {
    const refreshToken = urlParams.get('refreshToken') || '';
    tokens.value = {
      accessToken,
      refreshToken,
      expiresAt: Date.now() + 3600 * 1000, // 1 hour
    };

    // Clean URL
    const cleanUrl = new URL(window.location.href);
    ['accessToken', 'token', 'refreshToken', 'provider', 'isNewUser',
     'spotify_access_token', 'spotify_refresh_token', 'spotify_expires_in',
     'apple_music_access_token'].forEach(p => cleanUrl.searchParams.delete(p));
    window.history.replaceState({}, '', cleanUrl.toString());
  }

  // If we have tokens, verify they're still valid
  if (tokens.value) {
    try {
      const response = await api.get<{ user: User }>('/api/auth/me');
      if (response.data?.user) {
        user.value = response.data.user;
        // Check all music provider connections
        await checkProviderConnections();
      } else {
        // Token invalid, clear state
        await logout();
      }
    } catch (e) {
      // Token invalid or network error
      console.error('Auth verification failed:', e);
      await logout();
    }
  }

  isLoading.value = false;
}

/**
 * Login with email and password
 */
export async function login(credentials: LoginCredentials): Promise<boolean> {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.post<{
      user: User;
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }>('/api/auth/login', credentials);

    if (response.data) {
      user.value = response.data.user;
      tokens.value = {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
        expiresAt: Date.now() + response.data.expiresIn * 1000,
      };

      await checkProviderConnections();
      return true;
    }

    error.value = response.error || 'Login failed';
    return false;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Login failed';
    return false;
  } finally {
    isLoading.value = false;
  }
}

/**
 * Register a new account
 */
export async function register(data: RegisterData): Promise<boolean> {
  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.post<{
      user: User;
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }>('/api/auth/register', data);

    if (response.data) {
      user.value = response.data.user;
      tokens.value = {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
        expiresAt: Date.now() + response.data.expiresIn * 1000,
      };
      return true;
    }

    error.value = response.error || 'Registration failed';
    return false;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Registration failed';
    return false;
  } finally {
    isLoading.value = false;
  }
}

/**
 * Logout and clear all state
 */
export async function logout(): Promise<void> {
  try {
    if (tokens.value) {
      await api.post('/api/auth/logout');
    }
  } catch (e) {
    // Ignore logout errors
  }

  user.value = null;
  tokens.value = null;
  providerConnections.value = new Map();
  activeProvider.value = null;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Refresh access token
 */
export async function refreshAccessToken(): Promise<boolean> {
  if (!tokens.value?.refreshToken) return false;

  try {
    const response = await api.post<{
      accessToken: string;
      expiresIn: number;
    }>('/api/auth/refresh', {
      refreshToken: tokens.value.refreshToken,
    });

    if (response.data) {
      tokens.value = {
        ...tokens.value,
        accessToken: response.data.accessToken,
        expiresAt: Date.now() + response.data.expiresIn * 1000,
      };
      return true;
    }

    return false;
  } catch (e) {
    console.error('Token refresh failed:', e);
    return false;
  }
}

/**
 * Check all music provider connections
 */
export async function checkProviderConnections(): Promise<void> {
  if (!isAuthenticated.value) return;

  try {
    const response = await api.get<{
      providers: Array<{
        provider: MusicProvider;
        connected: boolean;
        accessToken?: string;
        refreshToken?: string;
        expiresAt?: number;
        userToken?: string; // For Apple Music
      }>;
      activeProvider?: MusicProvider;
    }>('/api/music/connections');

    if (response.data?.providers) {
      const newConnections = new Map<MusicProvider, ProviderConnection>();

      for (const p of response.data.providers) {
        newConnections.set(p.provider, {
          provider: p.provider,
          connected: p.connected,
          accessToken: p.accessToken,
          refreshToken: p.refreshToken,
          expiresAt: p.expiresAt,
          userToken: p.userToken,
        });
      }

      providerConnections.value = newConnections;

      // Set active provider (user's primary or first connected)
      if (response.data.activeProvider) {
        activeProvider.value = response.data.activeProvider;
      } else if (connectedProviders.value.length > 0) {
        activeProvider.value = connectedProviders.value[0];
      }
    }
  } catch (e) {
    console.error('Failed to check provider connections:', e);
  }
}

// Legacy alias for backward compatibility
export const checkSpotifyConnection = checkProviderConnections;

/**
 * Get auth URL for a provider (full URL with API base)
 */
export function getProviderAuthUrl(provider: MusicProvider): string {
  const baseUrl = import.meta.env.VITE_API_URL || '';
  const providerInfo = PROVIDERS.find(p => p.id === provider);
  const path = providerInfo?.authUrl || `/api/music/auth/${provider}`;
  return `${baseUrl}${path}`;
}

// Legacy alias
export function getSpotifyAuthUrl(): string {
  return getProviderAuthUrl('spotify');
}

/**
 * Wait for MusicKit JS to load
 */
async function waitForMusicKit(timeout = 10000): Promise<boolean> {
  if (typeof window.MusicKit !== 'undefined') return true;

  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      if (typeof window.MusicKit !== 'undefined') {
        resolve(true);
      } else if (Date.now() - start > timeout) {
        resolve(false);
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });
}

/**
 * Connect Apple Music via MusicKit JS
 */
export async function connectAppleMusic(): Promise<boolean> {
  // Wait for MusicKit to load (loaded async)
  const musicKitReady = await waitForMusicKit();
  if (!musicKitReady || typeof window.MusicKit === 'undefined') {
    console.error('MusicKit failed to load');
    error.value = 'Apple Music is not available. Please try again.';
    return false;
  }

  try {
    // Get developer token from backend
    const tokenResponse = await api.get<{ developerToken: string }>('/api/music/auth/apple_music/developer-token');
    if (!tokenResponse.data?.developerToken) {
      throw new Error('Failed to get Apple Music developer token');
    }

    // Configure MusicKit
    const music = await window.MusicKit.configure({
      developerToken: tokenResponse.data.developerToken,
      app: {
        name: 'Sherit',
        build: '1.0.0',
      },
    });

    // Authorize user with timeout (popup might be blocked or user might not complete)
    const authPromise = music.authorize();
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Authorization timed out. Please allow popups and try again.')), 60000)
    );

    const userToken = await Promise.race([authPromise, timeoutPromise]);

    // Save user token to backend
    const saveResponse = await api.post<{
      accessToken: string;
      refreshToken: string;
      provider: string;
      isNewUser: boolean;
      user: User;
    }>('/api/music/auth/apple_music/connect', {
      userToken,
    });

    if (saveResponse.data?.accessToken) {
      // Set auth tokens and user
      user.value = saveResponse.data.user;
      tokens.value = {
        accessToken: saveResponse.data.accessToken,
        refreshToken: saveResponse.data.refreshToken,
        expiresAt: Date.now() + 3600 * 1000, // 1 hour
      };

      // Update local state
      const newConnections = new Map(providerConnections.value);
      newConnections.set('apple_music', {
        provider: 'apple_music',
        connected: true,
        userToken,
      });
      providerConnections.value = newConnections;

      if (!activeProvider.value) {
        activeProvider.value = 'apple_music';
      }

      return true;
    }

    return false;
  } catch (e) {
    console.error('Failed to connect Apple Music:', e);
    return false;
  }
}

/**
 * Disconnect a music provider
 */
export async function disconnectProvider(provider: MusicProvider): Promise<boolean> {
  try {
    const response = await api.delete<{ success: boolean }>(`/api/music/connections/${provider}`);

    if (response.data?.success) {
      const newConnections = new Map(providerConnections.value);
      newConnections.delete(provider);
      providerConnections.value = newConnections;

      // If this was the active provider, switch to another
      if (activeProvider.value === provider) {
        activeProvider.value = connectedProviders.value[0] || null;
      }

      return true;
    }

    return false;
  } catch (e) {
    console.error(`Failed to disconnect ${provider}:`, e);
    return false;
  }
}

/**
 * Set the active music provider for search/playback
 */
export function setActiveProvider(provider: MusicProvider): void {
  if (providerConnections.value.get(provider)?.connected) {
    activeProvider.value = provider;
    // Persist to backend
    api.put('/api/users/preferences', { activeProvider: provider }).catch(console.error);
  }
}

/**
 * Update user profile
 */
export async function updateProfile(updates: Partial<User>): Promise<boolean> {
  if (!isAuthenticated.value) return false;

  try {
    const response = await api.put<{ user: User }>('/api/users/profile', updates);
    if (response.data?.user) {
      user.value = response.data.user;
      return true;
    }
    return false;
  } catch (e) {
    console.error('Profile update failed:', e);
    return false;
  }
}
